import isBoolean from 'lodash/isBoolean';

import {ChartType, DATASET_MIXPANEL} from 'common/report/constants';
import {
  ChartPlotStyle,
  DisplayOptions,
  InsightsBookmarkParams,
  InsightsResourceType,
  TimeClause,
} from 'common/report/util/insights/types';
import {isChartTypeDisabled} from 'common/report/util/insights/utils';
import {BaseDate, DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {
  END_OF_TODAY,
  END_OF_YESTERDAY,
  START_OF_TODAY,
  START_OF_YESTERDAY,
} from 'common/widgets/date-range-picker/models/presets';
import {insightsFilterAttrsFromPropertyFilter} from 'common/widgets/property-filter-menu/models/insights-filter';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';

function getDisplayOptionOverridesForChartType(chartType: ChartType): Pick<DisplayOptions, 'chartType' | 'plotStyle'> {
  switch (chartType) {
    case ChartType.Bar:
      return {
        chartType: ChartType.Bar,
        plotStyle: ChartPlotStyle.Standard,
      };
    case ChartType.StackedBar:
      return {
        chartType: ChartType.Bar,
        plotStyle: ChartPlotStyle.Stacked,
      };
    case ChartType.Line:
      return {
        chartType: ChartType.Line,
        plotStyle: ChartPlotStyle.Standard,
      };
    case ChartType.StackedLine:
      return {
        chartType: ChartType.Line,
        plotStyle: ChartPlotStyle.Stacked,
      };
    case ChartType.Pie:
      return {
        chartType: ChartType.Pie,
        plotStyle: ChartPlotStyle.Standard,
      };
    case ChartType.Table:
      return {
        chartType: ChartType.Table,
        plotStyle: ChartPlotStyle.Standard,
      };
    default: {
      throw new Error(`unknown Insights chart type "${chartType}"`);
    }
  }
}

// It's important that these override methods return a whole bookmark params
// so that it does not require additional knowledge of the bookmark format to use effectively
export function getBookmarkParamsWithChartType(
  bookmarkParams: InsightsBookmarkParams,
  chartTypeOverride: ChartType,
): InsightsBookmarkParams {
  if (!isChartTypeDisabled(bookmarkParams, chartTypeOverride)) {
    return {
      ...bookmarkParams,
      displayOptions: {
        ...bookmarkParams.displayOptions,
        ...getDisplayOptionOverridesForChartType(chartTypeOverride),
      },
    };
  }

  return bookmarkParams;
}

export function getTimeClauseForDateRange(dateRange: DateRange): TimeClause {
  // rewrite special presets
  if (dateRange.type === DateRangeType.Between && dateRange.from === START_OF_TODAY && dateRange.to === END_OF_TODAY) {
    return {
      dataset: DATASET_MIXPANEL,
      dateRangeType: DateRangeType.Since,
      value: START_OF_TODAY,
      window: null,
      unit: TimeUnit.Hour,
    };
  } else if (
    dateRange.type === DateRangeType.Between &&
    dateRange.from === START_OF_YESTERDAY &&
    dateRange.to === END_OF_YESTERDAY
  ) {
    return {
      dataset: DATASET_MIXPANEL,
      dateRangeType: DateRangeType.Between,
      value: [START_OF_YESTERDAY, END_OF_YESTERDAY],
      window: null,
      unit: TimeUnit.Hour,
    };
  }

  const {type, from, to, window} = dateRange;
  const value = (() => {
    switch (type) {
      case DateRangeType.Since:
        return [from] as [BaseDate];
      case DateRangeType.On:
        return [from, from] as [BaseDate, BaseDate];
      case DateRangeType.Between:
        return [from, to] as [BaseDate, BaseDate];
    }
    return null;
  })();
  const unit = ((): TimeUnit => {
    if (type === DateRangeType.On) {
      return TimeUnit.Hour;
    }
    if (type === DateRangeType.RelativeAfter) {
      // when a single unit is selected, avoid displaying
      // a single data point by breaking down to a smaller unit
      if (window.value === 1) {
        switch (window.unit) {
          case TimeUnit.Day:
            return TimeUnit.Hour;
          case TimeUnit.Week:
            return TimeUnit.Day;
          case TimeUnit.Month:
            return TimeUnit.Week;
        }
      }
      return window.unit;
    }
    return TimeUnit.Day;
  })();
  return {
    ...(isBoolean(dateRange.rolling) ? {rolling: dateRange.rolling} : {}),
    dataset: DATASET_MIXPANEL,
    dateRangeType: type,
    unit,
    value,
    ...(window ? {window} : {}),
  };
}

// It's important that these override methods return a whole bookmark params
// so that it does not require additional knowledge of the bookmark format to use effectively
export function getBookmarkParamsWithDateRange(
  bookmarkParams: InsightsBookmarkParams,
  dateRangeOverride: DateRange,
  timeUnitOverride?: TimeUnit,
): InsightsBookmarkParams {
  const newTimeClause = getTimeClauseForDateRange(dateRangeOverride);
  if (timeUnitOverride) {
    newTimeClause.unit = timeUnitOverride;
  }
  // As of writing, arb doesn't support Cohorts over Time queries with anything finer than Day for perf reasons
  const hasCohortsOverTime = bookmarkParams.sections.show.some(
    (showClause) => showClause.resourceType === InsightsResourceType.Cohorts,
  );
  if (hasCohortsOverTime && [TimeUnit.Hour, TimeUnit.Minute].includes(newTimeClause.unit)) {
    newTimeClause.unit = TimeUnit.Day;
  }
  return {
    ...bookmarkParams,
    sections: {
      ...bookmarkParams.sections,
      time: [newTimeClause],
    },
  };
}

// It's important that these override methods return a whole bookmark params
// so that it does not require additional knowledge of the bookmark format to use effectively
export function getBookmarkParamsWithAdditionalFilters(
  bookmarkParams: InsightsBookmarkParams,
  filters: PropertyFilter[],
): InsightsBookmarkParams {
  // Note: getBookmarkParamsWithAdditionalFilters() does not have dedicated tests because
  // it is primarily a wrapper around `insightsFilterAttrsFromPropertyFilter` that is already covered by tests elsewhere
  const insightsFilters: TODO[] = filters.map((propertyFilter) => {
    return insightsFilterAttrsFromPropertyFilter({dataset: DATASET_MIXPANEL, ...propertyFilter});
  });
  return {
    ...bookmarkParams,
    sections: {
      ...bookmarkParams.sections,
      filter: [...bookmarkParams.sections.filter, ...insightsFilters],
    },
  };
}
